/**
 * Production results (MB): field rollup from persisted per-layer last_result,
 * plus read-only per-layer development passports and ZIP export.
 */
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import type { ScenarioLayer } from '@/api/scenarioLayers';
import { scenariosApi } from '@/api/scenarios';
import { scenarioLayersApi } from '@/api/scenarioLayers';
import { scenarioWellsApi } from '@/api/scenarioWells';
import { ActiveWellsBarChart } from '@/components/Scenarios/ActiveWellsBarChart';
import { FieldProductionChart } from '@/components/Scenarios/FieldProductionChart';
import { MaterialBalanceLayerPassport } from '@/components/Scenarios/MaterialBalanceLayerPassport';
import { contourColorForIndex } from '@/components/Scenarios/WellsFieldMap';
import type { MaterialBalanceConfigResponse } from '@/types/materialBalance';
import type { ScenarioWell } from '@/types/scenarioWell';
import {
  aggregateMbFieldResults,
  toMbFieldPoints,
} from '@/utils/materialBalanceFieldAggregate';
import { buildLayerPassportViewModel } from '@/utils/materialBalanceLayerPassport';
import { exportMaterialBalanceResultsZip } from '@/utils/materialBalanceResultsExport';

export interface MaterialBalanceFieldResultsProps {
  scenarioId: string;
}

type LayerMbRow = {
  layer: ScenarioLayer;
  mb: MaterialBalanceConfigResponse | null;
};

export const MaterialBalanceFieldResults: React.FC<MaterialBalanceFieldResultsProps> = ({
  scenarioId,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<LayerMbRow[]>([]);
  const [wells, setWells] = useState<ScenarioWell[]>([]);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [layers, allWells] = await Promise.all([
          scenarioLayersApi.getScenarioLayers(scenarioId),
          scenarioWellsApi.getScenarioWells(scenarioId).catch(() => [] as ScenarioWell[]),
        ]);
        if (cancelled) return;
        const mbRows = await Promise.all(
          (layers || []).map(async (l) => {
            try {
              const mb = await scenariosApi.getMaterialBalance(scenarioId, l.scenario_layer_id);
              return { layer: l, mb } satisfies LayerMbRow;
            } catch {
              return { layer: l, mb: null } satisfies LayerMbRow;
            }
          }),
        );
        if (cancelled) return;
        setRows(mbRows);
        setWells(allWells || []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load material balance results');
          setRows([]);
          setWells([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scenarioId]);

  const results = useMemo(() => rows.map((r) => r.mb?.last_result ?? null), [rows]);
  const agg = useMemo(() => aggregateMbFieldResults(results), [results]);
  const fieldPoints = useMemo(() => toMbFieldPoints(agg.field), [agg.field]);
  const layerTotal = rows.length || agg.layerCount;

  const passports = useMemo(
    () => rows.map((r) => buildLayerPassportViewModel(r.layer, r.mb, wells)),
    [rows, wells],
  );

  const firstResultIndex = useMemo(
    () => passports.findIndex((p) => p.hasResult),
    [passports],
  );

  const handleExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      await exportMaterialBalanceResultsZip({
        scenarioId,
        agg,
        passports,
      });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, [agg, exporting, passports, scenarioId]);

  if (loading) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading applied material-balance results…
        </Typography>
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!rows.length) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No reservoirs on this scenario yet. Add layers on Reservoirs, then configure material balance.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, flex: 1 }}>
          {agg.field.length
            ? `Field total from ${agg.layersWithResult} of ${agg.layerCount || layerTotal} ${(agg.layerCount || layerTotal) === 1 ? 'layer' : 'layers'} with applied results (read-only).`
            : `No applied results yet (${layerTotal} ${(layerTotal === 1 ? 'layer' : 'layers')}). Passports still show configuration.`}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            exporting ? <CircularProgress size={14} color="inherit" /> : <FileDownloadOutlinedIcon />
          }
          disabled={exporting}
          onClick={() => void handleExport()}
          sx={{ flexShrink: 0 }}
        >
          {exporting ? 'Exporting…' : 'Export results'}
        </Button>
      </Stack>

      {exportError && (
        <Alert severity="error" onClose={() => setExportError(null)}>
          {exportError}
        </Alert>
      )}

      <Stack spacing={1.5}>
        {agg.field.length ? (
          <>
            <Box sx={{ width: '100%' }}>
              <FieldProductionChart
                field={fieldPoints}
                xDomain={agg.xDomain}
                height={320}
                extractableOilKt={agg.extractableOilKt}
                emptyHint="No applied results."
              />
            </Box>
            <Box sx={{ width: '100%' }}>
              <ActiveWellsBarChart field={agg.field} xDomain={agg.xDomain} height={240} />
            </Box>
          </>
        ) : (
          <Alert severity="info">
            No applied material-balance results yet ({layerTotal}{' '}
            {layerTotal === 1 ? 'layer' : 'layers'}). Run material balance on Reservoir
            configuration for each layer. Passports below still show configuration inputs.
          </Alert>
        )}

        <Box sx={{ pt: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Reservoir development passports
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            Per-reservoir read-only summary of configuration and applied forecast — for concept
            review. Edit wells and properties on Reservoir configuration.
          </Typography>
          <Stack spacing={1.25}>
            {passports.map((p, index) => (
              <MaterialBalanceLayerPassport
                key={p.layerId}
                passport={p}
                layerColor={contourColorForIndex(index)}
                defaultExpanded={index === firstResultIndex}
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};
